import { FEATURE_CODE, PRODUCT_CODE } from '../constants';
import { CLIENT_ACCESS_QUESTION, REGULATED_DATA_QUESTION, TEAM_SIZE_QUESTION } from './questions';
import { PROFILE_CATEGORY, type WorkspaceProfile } from './types';

/**
 * Perfiles de servicios profesionales, empresa, salud, creatividad y
 * organizaciones.
 *
 * Los de ingeniería, tecnología y construcción viven en `engineering.ts`.
 */
export const BUSINESS_PROFILES: WorkspaceProfile[] = [
  // ---------------------------------------------------------------------------
  // Servicios profesionales
  // ---------------------------------------------------------------------------
  {
    code: 'LEGAL',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.PROFESSIONAL_SERVICES,
    name: 'Abogado / Despacho jurídico',
    description: 'Expedientes por cliente, contratos y escritos con trazabilidad de accesos.',
    icon: '⚖️',
    sortOrder: 210,
    terminology: {
      workspace: 'Despacho',
      folder: 'Expediente',
      folderPlural: 'Expedientes',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      { name: 'Clientes' },
      { name: 'Expedientes', children: [{ name: 'Activos' }, { name: 'Archivados' }] },
      { name: 'Contratos', children: [{ name: 'Plantillas' }, { name: 'Firmados' }] },
      { name: 'Escritos y demandas' },
      { name: 'Poderes y documentos notariales' },
      { name: 'Jurisprudencia y doctrina' },
      { name: 'Facturación' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.PASSWORD_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.FILE_VERSIONS,
    ],
    metadataFields: [
      { key: 'case_number', label: 'Número de expediente', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'BOTH' },
      { key: 'counterparty', label: 'Contraparte', type: 'text', appliesTo: 'FOLDER' },
      { key: 'court', label: 'Juzgado / Tribunal', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'practice_area',
        label: 'Materia',
        type: 'select',
        options: ['Civil', 'Penal', 'Laboral', 'Mercantil', 'Familia', 'Administrativo', 'Fiscal'],
        appliesTo: 'FOLDER',
      },
      { key: 'due_date', label: 'Fecha de vencimiento', type: 'date', appliesTo: 'BOTH' },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'practice_areas',
        label: '¿En qué áreas ejerces?',
        type: 'multiselect',
        options: [
          { value: 'civil', label: 'Civil' },
          { value: 'criminal', label: 'Penal' },
          { value: 'labor', label: 'Laboral' },
          { value: 'commercial', label: 'Mercantil' },
          { value: 'family', label: 'Familia' },
          { value: 'administrative', label: 'Administrativo' },
          { value: 'tax', label: 'Fiscal' },
        ],
      },
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: true,
      expiresInDays: 7,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'docx', 'doc', 'jpg', 'zip'],
    complianceNote:
      'Los documentos pueden estar sujetos a secreto profesional. Se activan por defecto enlaces con contraseña, caducidad y registro de accesos.',
  },
  {
    code: 'ACCOUNTING',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.PROFESSIONAL_SERVICES,
    name: 'Contabilidad / Fiscal',
    description: 'Carpeta por cliente y periodo fiscal, con retención y auditoría de accesos.',
    icon: '📊',
    sortOrder: 220,
    terminology: {
      workspace: 'Despacho contable',
      folder: 'Carpeta',
      folderPlural: 'Carpetas',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Clientes',
        children: [
          { name: 'Declaraciones' },
          { name: 'Nómina' },
          { name: 'Facturación' },
          { name: 'Estados financieros' },
          { name: 'Conciliaciones bancarias' },
          { name: 'Soportes contables' },
        ],
      },
      { name: 'Cierres anuales' },
      { name: 'Requerimientos de la autoridad fiscal' },
      { name: 'Contratos y encargos' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.PASSWORD_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.HISTORY_DAYS,
    ],
    metadataFields: [
      {
        key: 'tax_id',
        label: 'NIT / RFC / Identificación fiscal',
        type: 'text',
        appliesTo: 'BOTH',
      },
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'BOTH' },
      { key: 'fiscal_period', label: 'Periodo fiscal', type: 'text', appliesTo: 'BOTH' },
      {
        key: 'document_type',
        label: 'Tipo de documento',
        type: 'select',
        options: [
          'Declaración',
          'Factura',
          'Nómina',
          'Estado financiero',
          'Soporte',
          'Requerimiento',
        ],
        appliesTo: 'FILE',
      },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'client_count',
        label: '¿Cuántos clientes atiendes aproximadamente?',
        type: 'select',
        options: [
          { value: '1-10', label: 'Hasta 10' },
          { value: '11-50', label: '11 a 50' },
          { value: '50+', label: 'Más de 50' },
        ],
      },
      CLIENT_ACCESS_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: true,
      expiresInDays: 15,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'xlsx', 'xml', 'csv', 'docx'],
    complianceNote:
      'La información fiscal suele tener obligaciones de conservación. Revisa la retención de papelera y el historial de versiones de tu plan.',
  },
  {
    code: 'AUDIT',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.PROFESSIONAL_SERVICES,
    name: 'Auditoría y cumplimiento',
    description: 'Papeles de trabajo, evidencias y hallazgos por encargo, con trazabilidad total.',
    icon: '🔍',
    sortOrder: 230,
    terminology: {
      workspace: 'Firma',
      folder: 'Encargo',
      folderPlural: 'Encargos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Encargos',
        children: [
          { name: 'Planeación' },
          { name: 'Papeles de trabajo' },
          { name: 'Evidencias' },
          { name: 'Pruebas y muestreo' },
          { name: 'Hallazgos y observaciones' },
          { name: 'Informes' },
        ],
      },
      { name: 'Clientes' },
      { name: 'Archivo permanente' },
      { name: 'Programas y normativa de auditoría' },
      { name: 'Seguimiento de recomendaciones' },
      { name: 'Independencia y ética' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.PASSWORD_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.HISTORY_DAYS,
    ],
    metadataFields: [
      { key: 'engagement_code', label: 'Código de encargo', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Cliente auditado', type: 'text', appliesTo: 'BOTH' },
      { key: 'period', label: 'Periodo auditado', type: 'text', appliesTo: 'BOTH' },
      {
        key: 'audit_type',
        label: 'Tipo de auditoría',
        type: 'select',
        options: [
          'Financiera',
          'Interna',
          'Cumplimiento',
          'Operativa',
          'Sistemas / TI',
          'Forense',
          'Calidad (ISO)',
        ],
        appliesTo: 'FOLDER',
      },
      {
        key: 'risk_level',
        label: 'Nivel de riesgo',
        type: 'select',
        options: ['Alto', 'Medio', 'Bajo'],
        appliesTo: 'BOTH',
      },
      {
        key: 'finding_status',
        label: 'Estado del hallazgo',
        type: 'select',
        options: ['Abierto', 'En proceso', 'Cerrado'],
        appliesTo: 'FILE',
      },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'audit_types',
        label: '¿Qué auditorías realizas?',
        type: 'multiselect',
        options: [
          { value: 'financial', label: 'Financiera' },
          { value: 'internal', label: 'Interna' },
          { value: 'compliance', label: 'Cumplimiento normativo' },
          { value: 'operational', label: 'Operativa' },
          { value: 'it', label: 'Sistemas / TI' },
          { value: 'forensic', label: 'Forense' },
          { value: 'quality', label: 'Calidad y certificaciones ISO' },
        ],
      },
      {
        key: 'chain_of_custody',
        label: '¿Manejas evidencia que requiere cadena de custodia?',
        type: 'boolean',
        helpText: 'Destacaremos el registro de accesos y los permisos por carpeta.',
      },
      REGULATED_DATA_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: true,
      expiresInDays: 7,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'xlsx', 'docx', 'csv', 'zip'],
    complianceNote:
      'Los papeles de trabajo suelen estar sujetos a plazos de conservación y a deber de confidencialidad. Se activan por defecto enlaces con contraseña, caducidad y registro de accesos.',
  },
  {
    code: 'CONSULTING',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.PROFESSIONAL_SERVICES,
    name: 'Consultoría / Freelance',
    description: 'Propuestas, entregables y contratos organizados por cliente.',
    icon: '💼',
    sortOrder: 240,
    terminology: {
      workspace: 'Espacio de trabajo',
      folder: 'Carpeta',
      folderPlural: 'Carpetas',
      file: 'Archivo',
      filePlural: 'Archivos',
    },
    folderTemplate: [
      { name: 'Clientes' },
      { name: 'Propuestas' },
      { name: 'Contratos' },
      { name: 'Entregables' },
      { name: 'Informes' },
      { name: 'Facturación' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.SHARED_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.CUSTOM_BRANDING,
    ],
    metadataFields: [
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'BOTH' },
      { key: 'engagement', label: 'Proyecto / Encargo', type: 'text', appliesTo: 'BOTH' },
    ],
    onboardingQuestions: [TEAM_SIZE_QUESTION, CLIENT_ACCESS_QUESTION],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'docx', 'pptx', 'xlsx'],
  },

  // ---------------------------------------------------------------------------
  // Empresa y operaciones
  // ---------------------------------------------------------------------------
  {
    code: 'GENERAL_BUSINESS',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.BUSINESS,
    name: 'Empresa (general)',
    description: 'Estructura estándar para pequeñas empresas y equipos de trabajo.',
    icon: '🏬',
    sortOrder: 310,
    terminology: {
      workspace: 'Empresa',
      folder: 'Carpeta',
      folderPlural: 'Carpetas',
      file: 'Archivo',
      filePlural: 'Archivos',
    },
    folderTemplate: [
      { name: 'Documentos corporativos' },
      { name: 'Contratos' },
      { name: 'Facturación' },
      { name: 'Recursos humanos' },
      { name: 'Marketing' },
      { name: 'Compartido con el equipo' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.ORGANIZATIONS,
      FEATURE_CODE.TEAMS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.AUDIT_LOGS,
    ],
    metadataFields: [{ key: 'department', label: 'Departamento', type: 'text', appliesTo: 'BOTH' }],
    onboardingQuestions: [TEAM_SIZE_QUESTION, CLIENT_ACCESS_QUESTION],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'docx', 'xlsx', 'pptx', 'zip'],
  },
  {
    code: 'HR_RECRUITING',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.BUSINESS,
    name: 'Recursos humanos',
    description: 'Expedientes de personal, selección y contratos con acceso restringido.',
    icon: '👥',
    sortOrder: 320,
    terminology: {
      workspace: 'Empresa',
      folder: 'Expediente',
      folderPlural: 'Expedientes',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Expedientes de personal',
        children: [
          { name: 'Contratos' },
          { name: 'Documentos de identidad' },
          { name: 'Certificaciones y estudios' },
          { name: 'Evaluaciones de desempeño' },
        ],
      },
      {
        name: 'Selección',
        children: [{ name: 'Vacantes' }, { name: 'Hojas de vida' }, { name: 'Pruebas' }],
      },
      { name: 'Nómina y prestaciones' },
      { name: 'Capacitación' },
      { name: 'Políticas y reglamento interno' },
      { name: 'Seguridad y salud en el trabajo' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.ADVANCED_PERMISSIONS,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.PASSWORD_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
    ],
    metadataFields: [
      { key: 'employee_id', label: 'Código de empleado', type: 'text', appliesTo: 'BOTH' },
      { key: 'department', label: 'Departamento', type: 'text', appliesTo: 'BOTH' },
      { key: 'position', label: 'Cargo', type: 'text', appliesTo: 'FOLDER' },
      { key: 'hire_date', label: 'Fecha de ingreso', type: 'date', appliesTo: 'FOLDER' },
      {
        key: 'document_type',
        label: 'Tipo de documento',
        type: 'select',
        options: [
          'Contrato',
          'Hoja de vida',
          'Certificación',
          'Evaluación',
          'Incapacidad',
          'Liquidación',
        ],
        appliesTo: 'FILE',
      },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'hr_scope',
        label: '¿Qué procesos gestionas aquí?',
        type: 'multiselect',
        options: [
          { value: 'records', label: 'Expedientes de personal' },
          { value: 'recruiting', label: 'Selección y contratación' },
          { value: 'payroll', label: 'Nómina' },
          { value: 'training', label: 'Capacitación' },
          { value: 'performance', label: 'Evaluación de desempeño' },
        ],
      },
    ],
    sharingDefaults: {
      requirePassword: true,
      expiresInDays: 7,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'docx', 'xlsx', 'jpg'],
    complianceNote:
      'Los expedientes de personal contienen datos personales. Limita el acceso mediante permisos por carpeta y evita compartirlos por enlace público salvo que sea imprescindible.',
  },
  {
    code: 'REAL_ESTATE',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.BUSINESS,
    name: 'Inmobiliaria',
    description: 'Inmuebles, escrituras, avalúos y material de promoción.',
    icon: '🏢',
    sortOrder: 330,
    terminology: {
      workspace: 'Inmobiliaria',
      folder: 'Inmueble',
      folderPlural: 'Inmuebles',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      { name: 'Inmuebles', children: [{ name: 'Fotografías' }, { name: 'Documentación' }] },
      { name: 'Contratos' },
      { name: 'Escrituras' },
      { name: 'Avalúos' },
      { name: 'Clientes' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.PHOTO_GALLERY,
      FEATURE_CODE.SHARED_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
    ],
    metadataFields: [
      { key: 'property_code', label: 'Código del inmueble', type: 'text', appliesTo: 'FOLDER' },
      { key: 'address', label: 'Dirección', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'operation',
        label: 'Operación',
        type: 'select',
        options: ['Venta', 'Arriendo', 'Permuta'],
        appliesTo: 'FOLDER',
      },
    ],
    onboardingQuestions: [TEAM_SIZE_QUESTION, CLIENT_ACCESS_QUESTION],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'jpg', 'png', 'docx', 'mp4'],
  },
  {
    code: 'INSURANCE',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.BUSINESS,
    name: 'Seguros',
    description: 'Pólizas, siniestros y documentación de clientes.',
    icon: '🛡️',
    sortOrder: 340,
    terminology: {
      workspace: 'Agencia',
      folder: 'Expediente',
      folderPlural: 'Expedientes',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      { name: 'Clientes' },
      {
        name: 'Pólizas',
        children: [{ name: 'Vigentes' }, { name: 'Renovaciones' }, { name: 'Canceladas' }],
      },
      {
        name: 'Siniestros',
        children: [
          { name: 'Reportes' },
          { name: 'Peritajes' },
          { name: 'Soportes' },
          { name: 'Finiquitos' },
        ],
      },
      { name: 'Cotizaciones' },
      { name: 'Comisiones' },
      { name: 'Compañías aseguradoras' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.PASSWORD_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
    ],
    metadataFields: [
      { key: 'policy_number', label: 'Número de póliza', type: 'text', appliesTo: 'BOTH' },
      { key: 'client', label: 'Asegurado', type: 'text', appliesTo: 'BOTH' },
      {
        key: 'insurance_type',
        label: 'Ramo',
        type: 'select',
        options: [
          'Vida',
          'Salud',
          'Autos',
          'Hogar',
          'Empresarial',
          'Responsabilidad civil',
          'Transporte',
        ],
        appliesTo: 'FOLDER',
      },
      { key: 'claim_number', label: 'Número de siniestro', type: 'text', appliesTo: 'FILE' },
      { key: 'expiry_date', label: 'Vencimiento', type: 'date', appliesTo: 'FOLDER' },
    ],
    onboardingQuestions: [TEAM_SIZE_QUESTION, CLIENT_ACCESS_QUESTION],
    sharingDefaults: {
      requirePassword: true,
      expiresInDays: 15,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'jpg', 'docx', 'xlsx'],
    complianceNote:
      'Las pólizas y siniestros contienen datos personales y, en el ramo de salud, datos sensibles. Comparte siempre con contraseña y caducidad.',
  },

  // ---------------------------------------------------------------------------
  // Salud
  // ---------------------------------------------------------------------------
  {
    code: 'HEALTHCARE',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.HEALTH,
    name: 'Salud',
    description: 'Consultorios y clínicas: estudios, consentimientos y administración.',
    icon: '🩺',
    sortOrder: 410,
    terminology: {
      workspace: 'Consultorio',
      folder: 'Carpeta',
      folderPlural: 'Carpetas',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      { name: 'Pacientes' },
      { name: 'Estudios e imágenes' },
      { name: 'Consentimientos informados' },
      { name: 'Facturación' },
      { name: 'Administración' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.PASSWORD_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.ADVANCED_PERMISSIONS,
    ],
    metadataFields: [
      { key: 'record_number', label: 'Número de historia', type: 'text', appliesTo: 'BOTH' },
      { key: 'study_date', label: 'Fecha del estudio', type: 'date', appliesTo: 'FILE' },
      {
        key: 'document_type',
        label: 'Tipo de documento',
        type: 'select',
        options: ['Estudio', 'Consentimiento', 'Receta', 'Informe', 'Facturación'],
        appliesTo: 'FILE',
      },
    ],
    onboardingQuestions: [TEAM_SIZE_QUESTION],
    sharingDefaults: {
      requirePassword: true,
      expiresInDays: 3,
      allowDownload: false,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'jpg', 'png', 'docx', 'zip'],
    complianceNote:
      'Los datos de salud son datos personales sensibles. Los enlaces se crean por defecto con contraseña, caducidad corta y descarga deshabilitada; verifica la normativa aplicable en tu país antes de compartir.',
  },

  // ---------------------------------------------------------------------------
  // Creatividad y medios
  // ---------------------------------------------------------------------------
  {
    code: 'CREATIVE',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.CREATIVE,
    name: 'Agencia creativa',
    description: 'Marcas, campañas y entregables con revisión de versiones.',
    icon: '🎨',
    sortOrder: 510,
    terminology: {
      workspace: 'Agencia',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Pieza',
      filePlural: 'Piezas',
    },
    folderTemplate: [
      { name: 'Clientes' },
      { name: 'Proyectos', children: [{ name: 'Propuestas' }, { name: 'Entregables' }] },
      { name: 'Brand assets' },
      { name: 'Recursos y plantillas' },
      { name: 'Contratos' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FILE_VERSIONS,
      FEATURE_CODE.CUSTOM_BRANDING,
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.SHARED_LINKS,
    ],
    metadataFields: [
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'BOTH' },
      { key: 'campaign', label: 'Campaña', type: 'text', appliesTo: 'FOLDER' },
      { key: 'version', label: 'Versión', type: 'text', appliesTo: 'FILE' },
    ],
    onboardingQuestions: [TEAM_SIZE_QUESTION, CLIENT_ACCESS_QUESTION],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 14,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['psd', 'ai', 'pdf', 'png', 'jpg', 'mp4'],
  },

  // ---------------------------------------------------------------------------
  // Educación y organizaciones
  // ---------------------------------------------------------------------------
  {
    code: 'EDUCATION',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.EDUCATION,
    name: 'Educación y formación',
    description: 'Material de clase, evaluaciones y documentación académica.',
    icon: '🏫',
    sortOrder: 610,
    terminology: {
      workspace: 'Institución',
      folder: 'Curso',
      folderPlural: 'Cursos',
      file: 'Material',
      filePlural: 'Materiales',
    },
    folderTemplate: [
      {
        name: 'Cursos',
        children: [
          { name: 'Planes de clase' },
          { name: 'Material de apoyo' },
          { name: 'Evaluaciones' },
          { name: 'Trabajos de estudiantes' },
        ],
      },
      {
        name: 'Documentación académica',
        children: [{ name: 'Actas' }, { name: 'Certificados' }],
      },
      { name: 'Recursos compartidos' },
      { name: 'Administración' },
      { name: 'Comunicaciones' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.SHARED_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.TEAMS,
    ],
    metadataFields: [
      { key: 'course', label: 'Curso / Asignatura', type: 'text', appliesTo: 'BOTH' },
      { key: 'term', label: 'Periodo académico', type: 'text', appliesTo: 'BOTH' },
      { key: 'grade_level', label: 'Nivel / Grado', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'resource_type',
        label: 'Tipo de recurso',
        type: 'select',
        options: ['Plan de clase', 'Material', 'Evaluación', 'Acta', 'Certificado'],
        appliesTo: 'FILE',
      },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'institution_type',
        label: '¿Qué tipo de institución eres?',
        type: 'select',
        options: [
          { value: 'teacher', label: 'Docente independiente' },
          { value: 'school', label: 'Colegio' },
          { value: 'university', label: 'Universidad' },
          { value: 'training', label: 'Centro de formación' },
          { value: 'online', label: 'Academia online' },
        ],
      },
      {
        key: 'shares_with_students',
        label: '¿Compartes material con estudiantes por enlace?',
        type: 'boolean',
      },
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 90,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'docx', 'pptx', 'xlsx', 'mp4', 'jpg'],
    complianceNote:
      'Si trabajas con menores de edad, restringe los enlaces públicos y revisa la normativa de protección de datos aplicable antes de compartir trabajos o fotografías.',
  },
  {
    code: 'NONPROFIT',
    productCode: PRODUCT_CODE.BUSINESS,
    category: PROFILE_CATEGORY.EDUCATION,
    name: 'ONG y fundaciones',
    description: 'Proyectos, donantes e informes de rendición de cuentas.',
    icon: '🤝',
    sortOrder: 620,
    terminology: {
      workspace: 'Organización',
      folder: 'Proyecto',
      folderPlural: 'Proyectos',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      {
        name: 'Proyectos',
        children: [
          { name: 'Propuestas' },
          { name: 'Convenios' },
          { name: 'Ejecución' },
          { name: 'Informes' },
        ],
      },
      { name: 'Donantes y cooperantes' },
      {
        name: 'Rendición de cuentas',
        children: [{ name: 'Informes financieros' }, { name: 'Auditorías' }],
      },
      { name: 'Beneficiarios' },
      { name: 'Comunicaciones y campañas' },
      { name: 'Documentos institucionales' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.FOLDER_SHARING,
      FEATURE_CODE.AUDIT_LOGS,
      FEATURE_CODE.SHARED_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
    ],
    metadataFields: [
      { key: 'project_code', label: 'Código de proyecto', type: 'text', appliesTo: 'BOTH' },
      { key: 'donor', label: 'Donante / Cooperante', type: 'text', appliesTo: 'FOLDER' },
      { key: 'period', label: 'Periodo', type: 'text', appliesTo: 'BOTH' },
      {
        key: 'report_type',
        label: 'Tipo de informe',
        type: 'select',
        options: ['Narrativo', 'Financiero', 'Auditoría', 'Monitoreo'],
        appliesTo: 'FILE',
      },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'nonprofit_scope',
        label: '¿En qué áreas trabajas?',
        type: 'multiselect',
        options: [
          { value: 'social', label: 'Desarrollo social' },
          { value: 'education', label: 'Educación' },
          { value: 'health', label: 'Salud' },
          { value: 'environment', label: 'Medio ambiente' },
          { value: 'humanrights', label: 'Derechos humanos' },
          { value: 'emergency', label: 'Ayuda humanitaria' },
        ],
      },
      REGULATED_DATA_QUESTION,
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'docx', 'xlsx', 'jpg', 'pptx'],
    complianceNote:
      'Los datos de beneficiarios pueden ser sensibles. Restringe su acceso por permisos y evita publicarlos en enlaces sin caducidad.',
  },
];
